import type { NoteName, Scale } from '@tunasong/schemas'
import { chordTones } from '../chord/chord-tones.js'
import { getNoteChromaticIndex } from '../note/note-lib.js'
import ScaleLib from '../scale/scale.js'

/**
 * The notes a melody may use over a chord, the note set (Bowie's rulings of 2026-10-23 and 2026-10-29, GH #2934):
 * every chord tone, plus the key's notes whose letter has no chord tone and whose pitch isn't a chord tone. Over E7
 * in C that brings in G♯ and drops G; over B♭ in C it drops B♮, which clashes with the root.
 *
 * It holds for any key, five notes or seven, and keeps every chord tone even when two share a letter. The roll's
 * theory cues (`noteRoles`, `fitsAt`) and the AI server's repair both use it, so "in the note set" means the same in
 * both places.
 *
 * Chord tones come first, as the chord spells them; then the key's other notes in scale order. With no key it is the
 * chord's tones; an unknown chord gives the key.
 */
export const chordNoteSet = (symbol: string, key: Scale | undefined): NoteName[] => {
  const keyNotes = key ? Array.from(new Set(ScaleLib.getNotes(key))) : []
  const tones = chordTones(symbol) ?? []
  const toneNames = Array.from(new Set(tones.map(tone => tone.name)))
  const toneLetters = new Set(toneNames.map(name => name[0]))
  const tonePitches = new Set(tones.map(tone => tone.pitchClass))
  /**
   * GH #2935 (Bowie): a ♭13 is spelled on the 6th letter, so it leaves the 5th's letter free. When the chord has no
   * 5th of its own (C7alt, C7b13), the key's natural 5th would sit a semitone under the ♭13; it is not in the set.
   */
  const root = tones.find(tone => tone.degree === 1)?.pitchClass
  const hasFlatThirteenWithoutFifth =
    root !== undefined &&
    tones.some(tone => tone.degree === 6 && tone.pitchClass === (root + 8) % 12) &&
    !tones.some(tone => tone.degree === 5)
  const clash = hasFlatThirteenWithoutFifth && root !== undefined ? (root + 7) % 12 : undefined
  const rest = keyNotes.filter(name => {
    const pitch = getNoteChromaticIndex(name)
    return !toneLetters.has(name[0]) && !tonePitches.has(pitch) && pitch !== clash
  })
  return [...toneNames, ...rest]
}
